import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Typography, Stack,
    Card,
    Box,
    CardContent,
    useMediaQuery,
    useTheme,
    Grid,
    Autocomplete,
    Button,
    Chip,
    TextField,
    Snackbar, ToggleButtonGroup, ToggleButton,
    Alert, CircularProgress
} from "@mui/material";
import Layout from "../../components/layout/Layout";
import { getData } from "../../services/apiService";
import Progress from "../../hooks/useProgressBar";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
// Charts
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
} from 'chart.js';
import { Line, Pie } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
);


const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [studentData, setStudentData] = useState([]);
    const [instituteData, setInstituteData] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [activeVoucherCount, setActiveVoucherCount] = useState([]);
    const [activeInstitute, setActiveInstitute] = useState([]);
    const [inActiveInstitute, setInActiveInstitute] = useState([]);
    const [periodFilter, setPeriodFilter] = useState("Last 30 days");
    const [statusFilter, setStatusFilter] = useState("All");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const isFetched = useRef(false);
    const navigate = useNavigate();
    const [revenueView, setRevenueView] = useState("MTD");
    const [revenueLoading, setRevenueLoading] = useState(false);
    const [openTickets, setOpenTickets] = useState(null);
    const [resolvedTickets, setResolvedTickets] = useState(null);
    const [panddingTickets, setPanddingTickets] = useState(null);
    const [allTickets, setAllTickets] = useState(null);
    const [collegeData, setCollegeData] = useState([])
    const [courseData, setCourseData] = useState([])
    const [selectedCollege, setSelectedCollege] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [yearFilter, setYearFilter] = useState('');

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());
    }, []);

    const handleRevenueViewChange = (_e, v) => v !== null && setRevenueView(v);

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isSm = useMediaQuery(theme.breakpoints.down("md"));
    const commonCardShadow = "rgba(149, 157, 165, 0.1) 0px 4px 8px";


    const [progressStats, setProgressStats] = useState({
        notStarted: 0,
        inProgress: 0,
        completed: 0
    });


    const progressChartData = {
        labels: ["Not Started", "In Progress", "Completed"],
        datasets: [
            {
                data: [
                    progressStats.notStarted,
                    progressStats.inProgress,
                    progressStats.completed
                ],
                backgroundColor: ["#EF5350", "#FFCA28", "#66BB6A"],
                borderWidth: 1
            }
        ]
    };

    // Fetch Dashboard Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData("/get-admin-dashboard-data");
            if (response) {
                setStudentData(response.students);
                setInstituteData(response.institute);
                setTotalRevenue(response.totalRevenue);
                setActiveVoucherCount(response.unusedVoucherCount);
                setInActiveInstitute(response.inActiveInstitute);
                setActiveInstitute(response.activeInstitute);
                setPanddingTickets(response.panddingTickets);
                setResolvedTickets(response.resolvedTickets);
                setOpenTickets(response.openTickets);
                setAllTickets(response.allTickets);
            }
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch data", error);
            setLoading(false);
        }
    };

    // const getStudentProgressStats = async () => {
    //     setLoading(true);
    //     try {
    //         const response = await getData("/get-student-progress-stats");
    //         if (response) {
    //             setProgressStats({
    //                 notStarted: parseFloat(response.notStarted) || 0,
    //                 inProgress: parseFloat(response.inProgress) || 0,
    //                 completed: parseFloat(response.completed) || 0
    //             });
    //         }
    //         setLoading(false);
    //     } catch (error) {
    //         console.error("Failed to fetch data", error);
    //         setLoading(false);
    //     }
    // };

    const getStudentProgressStats = async () => {

        try {
            const params = new URLSearchParams();

            if (selectedCollege?._id) {
                params.append("collegeId", selectedCollege._id);
            }

            if (selectedCourse?._id) {
                params.append("courseId", selectedCourse._id);
            }

            if (yearFilter) {
                params.append("year", yearFilter);
            }

            const response = await getData(`/get-student-progress-stats?${params.toString()}`);

            if (response) {
                setProgressStats({
                    notStarted: parseFloat(response.notStarted) || 0,
                    inProgress: parseFloat(response.inProgress) || 0,
                    completed: parseFloat(response.completed) || 0
                });
            }

        } catch (error) {
            console.error(error);

        }
    };

    const getCollegesForDropdown = async () => {
        try {
            const response = await getData("/get-colleges-for-dropdown");
            if (response) {
                setCollegeData(response)
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
        }
    };

    const getCoursesForDropdown = async () => {
        try {
            const response = await getData("/get-courses-for-dropdown");
            if (response) {
                setCourseData(response)
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
        }
    }

    // Revenue Analysis with filter
    const revenueAnalysis = useCallback(async (filter = revenueView) => {
        setRevenueLoading(true);
        try {
            const response = await getData(`/revenue-analysis?filter=${filter}`);
            if (response) {
                setRevenueData(response);
            }
        } catch (error) {
            console.error("Failed to fetch revenue data", error);
            setSnackbar({
                open: true,
                message: "Failed to load revenue data",
                severity: "error"
            });
        } finally {
            setRevenueLoading(false);
        }
    }, [revenueView]);

    // Then update your useEffect dependencies
    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
            getStudentProgressStats();
            getCollegesForDropdown();
            getCoursesForDropdown();
            revenueAnalysis();
            isFetched.current = true;
        }
    }, [revenueAnalysis]);

    useEffect(() => {
        if (isFetched.current) {
            getStudentProgressStats();
        }
    }, [selectedCollege?._id, selectedCourse?._id, yearFilter]);


    useEffect(() => {
        const currentYear = new Date().getFullYear().toString();
        setYearFilter(currentYear);
    }, []);

    useEffect(() => {
        if (isFetched.current) {
            revenueAnalysis(revenueView);
        }
    }, [revenueView, revenueAnalysis]);

    // Handle Export Data
    const handleExportData = async () => {
        setExportLoading(true);
        try {
            const params = new URLSearchParams({
                period: periodFilter,
                status: statusFilter,
                format: "csv"
            });
            const fileContent = await getData(`/export-data?${params.toString()}`);

            if (typeof fileContent === 'string') {
                const blob = new Blob([fileContent], {
                    type: 'text/csv'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'export-data.csv';
                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }, 100);

                setSnackbar({
                    open: true,
                    message: "Data exported successfully!",
                    severity: "success"
                });
            } else {
                throw new Error("Unexpected response format from server");
            }
        } catch (error) {
            console.error("Export error:", error);
            setSnackbar({
                open: true,
                message: "Failed to export data. Please try again.",
                severity: "error"
            });
        } finally {
            setExportLoading(false);
        }
    };

    // Dynamic Metric Cards
    const metricCards = [
        {
            title: "Total Institutes Onboarded",
            value: Array.isArray(instituteData) ? instituteData.length : instituteData || 0,
            icon: <BusinessIcon />,
            color: "#4B49AC",
            bgColor: "#E8E7FF",
        },
        {
            title: "Total Active Students",
            value: Array.isArray(studentData) ? studentData.length : studentData || 0,
            icon: <PeopleIcon />,
            color: "#4CAF50",
            bgColor: "#E8F5E8",
        },
        {
            title: "Revenue (MTD / YTD)",
            value: Array.isArray(totalRevenue) ? totalRevenue.length : totalRevenue || 0,
            icon: <AttachMoneyIcon />,
            color: "#FF7043",
            bgColor: "#FFF3E0",
        },
        {
            title: "Active Voucher",
            value: Array.isArray(activeVoucherCount) ? activeVoucherCount.length : activeVoucherCount || 0,
            icon: <AssignmentIcon />,
            color: "#9C27B0",
            bgColor: "#F3E5F5",
        },
    ];

    const lineChartData = useMemo(() => ({
        labels: revenueData.map((i) => i.month),
        datasets: [
            {
                label: "Revenue (₹K)",
                data: revenueData.map((i) => i.revenue),
                borderColor: "#4B49AC",
                backgroundColor: "rgba(75, 73, 172, 0.2)",
                fill: true,
                tension: 0.4,
                pointRadius: isXs ? 3 : isSm ? 4 : 5,
                pointHoverRadius: isXs ? 5 : isSm ? 6 : 7,
            },
        ],
    }), [revenueData, isSm, isXs]);

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: true, position: "top" },
                tooltip: {
                    backgroundColor: "#4B49AC",
                    titleFont: { size: isXs ? 10 : isSm ? 11 : 12 },
                    bodyFont: { size: isXs ? 10 : isSm ? 11 : 12 },
                    padding: isXs ? 8 : 10,
                },
            },
            scales: {
                x: { grid: { display: false } },
                y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        stepSize: 20,
                        callback: (value) => (`₹${value}K`),
                        font: { size: isXs ? 10 : isSm ? 11 : 12 },
                    },
                    grid: { color: "rgba(0,0,0,0.1)" },
                },
            },
            animation: { duration: 2000, easing: "easeOutQuart" },
        }),
        [isSm, isXs]
    );


    return (
        <Layout>
            {loading ? (
                <Progress />
            ) : (
                <div className="bg-gray-100 min-h-screen">
                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            p: { xs: 2, sm: 3 },
                            width: "100%",
                            maxWidth: "100%",
                            mx: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(1, minmax(0, 1fr))",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    md: "repeat(3, minmax(0, 1fr))",
                                    lg: "repeat(4, minmax(0, 1fr))",
                                    // xl: "repeat(5, minmax(0, 1fr))",
                                },
                                gap: { xs: 2, sm: 3 },
                            }}
                        >
                            {metricCards.map((card, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        height: "100%",
                                        minHeight: { xs: 120, sm: 140, md: 100 },
                                        borderRadius: 2,
                                        boxShadow: "rgba(149, 157, 165, 0.1) 0px 4px 8px",
                                        display: "flex",
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, width: "100%" }}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="flex-start"
                                        >
                                            {/* Card Text */}
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 0.5,
                                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                    }}
                                                >
                                                    {card.title}
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 700,
                                                        mb: 0.5,
                                                        fontSize: {
                                                            xs: "1.25rem",
                                                            sm: "1.5rem",
                                                            md: "1.75rem",
                                                        },
                                                    }}
                                                >
                                                    {card.value}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                                                >
                                                    {card.subtitle}
                                                </Typography>
                                            </Box>

                                            {/* Card Icon */}
                                            <Box
                                                sx={{
                                                    width: { xs: 36, sm: 40, md: 48 },
                                                    height: { xs: 36, sm: 40, md: 48 },
                                                    borderRadius: 2,
                                                    backgroundColor: card.bgColor,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: card.color,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {React.cloneElement(card.icon, {
                                                    fontSize: isXs ? "small" : isSm ? "medium" : "large",
                                                })}
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>

                        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }} alignItems="stretch">
                            {/* Organization Overview */}
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                                <Card
                                    sx={{
                                        borderRadius: 2,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                        boxShadow: commonCardShadow,
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            display: "flex",
                                            flexDirection: "column",
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            sx={{ mb: { xs: 2, sm: 3 }, gap: 1 }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.25,
                                                        fontSize: { xs: "1rem", sm: "1.25rem" },
                                                    }}
                                                >
                                                    Organization Overview
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                                                >
                                                    Overview of all onboarded institutes with status breakdown.
                                                </Typography>
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                            >
                                                <Autocomplete
                                                    size="small"
                                                    options={["Last 7 days", "Last 30 days", "This Quarter", "YTD"]}
                                                    value={periodFilter}
                                                    onChange={(event, newValue) =>
                                                        setPeriodFilter(newValue || "Last 30 days")
                                                    }
                                                    renderInput={(params) => <TextField {...params} label="Period" />}
                                                    sx={{ minWidth: { xs: "50%", sm: 160 } }}
                                                />
                                                <Autocomplete
                                                    size="small"
                                                    options={["All", "Active", "Pending"]}
                                                    value={statusFilter}
                                                    onChange={(event, newValue) => setStatusFilter(newValue || "All")}
                                                    renderInput={(params) => <TextField {...params} label="Status" />}
                                                    sx={{ minWidth: { xs: "50%", sm: 160 } }}
                                                />
                                            </Stack>
                                        </Stack>

                                        {/* Status Boxes */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: { xs: "column", sm: "row" },
                                                gap: { xs: 1.5, sm: 2 },
                                                mb: { xs: 2, sm: 3 },
                                            }}
                                        >
                                            {[
                                                {
                                                    label: "ACTIVE",
                                                    count: Array.isArray(activeInstitute)
                                                        ? activeInstitute.length
                                                        : activeInstitute || 0,
                                                    color: "#4CAF50",
                                                    bg: "#E8F5E8",
                                                    border: "#4CAF501A",
                                                },
                                                {
                                                    label: "PENDING",
                                                    count: Array.isArray(inActiveInstitute)
                                                        ? inActiveInstitute.length
                                                        : inActiveInstitute || 0,
                                                    color: "#FFC107",
                                                    bg: "#FFF8E1",
                                                    border: "#FFC1071A",
                                                },
                                            ].map((stat, i) => (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        flex: 1,
                                                        p: { xs: 1.25, sm: 1.75 },
                                                        borderRadius: 2,
                                                        backgroundColor: stat.bg,
                                                        border: `1px solid ${stat.border}`,
                                                        minWidth: { xs: "100%", sm: "auto" },
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                backgroundColor: stat.color,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: stat.color,
                                                                letterSpacing: 0.4,
                                                                fontSize: { xs: "0.68rem", sm: "0.72rem" },
                                                            }}
                                                        >
                                                            {stat.label}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 700,
                                                            mb: 0.25,
                                                            fontSize: { xs: "1rem", sm: "1.2rem" },
                                                        }}
                                                    >
                                                        {stat.count}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Box
                                            sx={{
                                                p: { xs: 1.25, sm: 1.75 },
                                                backgroundColor: "#F8F9FA",
                                                borderRadius: 2,
                                                border: "1px solid #ECECEC",
                                                mt: "auto",
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "flex-start", sm: "center" }}
                                                sx={{ gap: 1, mb: { xs: 1, sm: 1.5 } }}
                                            >
                                                <Typography variant="subtitle2" sx={{ color: "#1F1F1F" }}>
                                                    Quick Actions
                                                </Typography>
                                                <Chip
                                                    label="Bulk tasks available"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #E6E6E6",
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Stack>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={{ xs: 1, sm: 2 }}
                                                sx={{ flexWrap: "wrap" }}
                                            >
                                                <Button
                                                    onClick={() => navigate("/add-college")}
                                                    variant="outlined"
                                                    startIcon={<AddIcon fontSize="small" />}
                                                    size="small"
                                                    sx={{ borderColor: "#4B49AC", color: "#4B49AC" }}
                                                >
                                                    Add New
                                                </Button>

                                                {/* Export Button */}
                                                <Button
                                                    variant="outlined"
                                                    startIcon={
                                                        exportLoading ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <FileDownloadIcon fontSize="small" />
                                                        )
                                                    }
                                                    size="small"
                                                    sx={{ color: "#9C27B0", borderColor: "#9C27B0" }}
                                                    onClick={handleExportData}
                                                    disabled={exportLoading}
                                                >
                                                    {exportLoading ? "Exporting..." : "Export Data"}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Revenue Analysis */}
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                                <Card
                                    sx={{
                                        borderRadius: 2,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                        boxShadow: commonCardShadow,
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            display: "flex",
                                            flexDirection: "column",
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            sx={{ mb: { xs: 2, sm: 3 } }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        fontSize: { xs: "1rem", sm: "1.25rem" },
                                                    }}
                                                >
                                                    Revenue Analysis
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                                                >
                                                    Subscription Revenue Trend
                                                </Typography>
                                            </Box>
                                            <ToggleButtonGroup
                                                value={revenueView}
                                                exclusive
                                                onChange={handleRevenueViewChange}
                                                size="small"
                                            >
                                                <ToggleButton value="MTD" sx={{ px: { xs: 1.5, sm: 2 }, py: 0.5 }}>
                                                    <Typography variant="caption">MTD</Typography>
                                                </ToggleButton>
                                                <ToggleButton value="YTD" sx={{ px: { xs: 1.5, sm: 2 }, py: 0.5 }}>
                                                    <Typography variant="caption">YTD</Typography>
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Stack>

                                        <Box sx={{ position: "relative", mb: 2, flexGrow: 1, minHeight: 220 }}>
                                            {revenueLoading ? (
                                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : (
                                                <Line data={lineChartData} options={chartOptions} />
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            {/* Support Tickets */}
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                                <Card sx={{ borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", width: "100%", boxShadow: commonCardShadow }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: { xs: 2, sm: 3 } }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: "1rem", sm: "1.25rem" } }}>Support Tickets</Typography>
                                                <Typography variant="body2" color="text.secondary">Open, Pending, and Resolved requests from institutes</Typography>
                                            </Box>
                                            <Chip label="Live Updates" size="small" sx={{ backgroundColor: "#ffffff", color: "#2196F3", fontWeight: 600 }} />
                                        </Stack>

                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                                            <Box sx={{ flex: 1, backgroundColor: "#E3F2FD", p: 1.5, borderRadius: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Open Tickets</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{openTickets}</Typography>
                                                <Typography variant="caption" color="text.secondary">Require attention</Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, backgroundColor: "#FFF8E1", p: 1.5, borderRadius: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Pending</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{panddingTickets}</Typography>
                                                <Typography variant="caption" color="text.secondary">Awaiting response</Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, backgroundColor: "#E8F5E9", p: 1.5, borderRadius: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Resolved</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{resolvedTickets}</Typography>
                                                <Typography variant="caption" color="text.secondary">This month</Typography>
                                            </Box>
                                        </Stack>

                                        <Box sx={{ overflowX: "auto", mb: 2 }}>
                                            <Box sx={{ minWidth: { xs: 520, sm: 600 } }}>
                                                <Stack direction="row" spacing={2} sx={{ p: 1, backgroundColor: "#F5F5F5", borderRadius: 1 }}>
                                                    <Typography sx={{ flex: 1, fontWeight: 600 }}>Ticket ID</Typography>
                                                    <Typography sx={{ flex: 1, fontWeight: 600 }}>Institute</Typography>
                                                    <Typography sx={{ flex: 2, fontWeight: 600 }}>Subject</Typography>
                                                    <Typography sx={{ flex: 1, fontWeight: 600 }}>Status</Typography>
                                                </Stack>
                                                {allTickets?.map((ticket) => (
                                                    <Stack key={ticket.support_ticket_no} direction="row" spacing={2} sx={{ p: 1, borderBottom: "1px solid #E0E0E0", alignItems: "center" }}>
                                                        <Typography sx={{ flex: 1 }}>{ticket?.support_ticket_no}</Typography>
                                                        <Typography sx={{ flex: 1 }}>{ticket?.created_by?.college_id?.collegeName}</Typography>
                                                        <Typography sx={{ flex: 2 }}>{ticket?.title}</Typography>
                                                        <Chip
                                                            label={ticket?.status}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    ticket.status === "Open" ? "#FFEBEE" :
                                                                        ticket.status === "IN-REVIEW" ? "#FFF8E1" :
                                                                            "#E8F5E9",
                                                                color:
                                                                    ticket.status === "Open" ? "#F44336" :
                                                                        ticket.status === "IN-REVIEW" ? "#FFC107" :
                                                                            "#4CAF50",
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </Box>

                                        <Button variant="text" sx={{ alignSelf: "flex-start", color: "#4B49AC" }} onClick={() => navigate("/admin/support-ticket")}>View All Tickets</Button>
                                    </CardContent>
                                </Card>
                            </Grid>



                            {/* Student Progress Status */}
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                        boxShadow: "rgba(0,0,0,0.06) 0px 4px 12px",
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            flexGrow: 1,
                                        }}
                                    >
                                        {/* Header */}
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            sx={{ mb: 2, gap: 2 }}
                                        >
                                            {/* LEFT SIDE (Title) */}
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    Student Progress Status
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Overall learning progress distribution
                                                </Typography>
                                            </Box>

                                            {/* RIGHT SIDE (Filters) */}
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1.5}
                                                sx={{
                                                    width: "100%",
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "1fr",
                                                        sm: "repeat(2, minmax(0, 1fr))",
                                                        md: "repeat(3, minmax(0, 1fr))",
                                                    },
                                                    gap: 1.5,
                                                }}
                                            >
                                                {/* College */}
                                                <Autocomplete
                                                    size="small"
                                                    options={collegeData}
                                                    getOptionLabel={(option) => option.collegeName || ""}
                                                    value={selectedCollege}
                                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                                    onChange={(e, newValue) => setSelectedCollege(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="College" />
                                                    )}
                                                    sx={{ width: "100%" }}
                                                />

                                                {/* Course */}
                                                <Autocomplete
                                                    size="small"
                                                    options={courseData}
                                                    getOptionLabel={(option) => option.courseName || ""}
                                                    value={selectedCourse}
                                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                                    onChange={(e, newValue) => setSelectedCourse(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Course" />
                                                    )}
                                                    sx={{ width: "100%" }}
                                                />

                                                {/* Year */}
                                                <Autocomplete
                                                    size="small"
                                                    options={yearOptions}
                                                    getOptionLabel={(option) => option || ""}
                                                    value={yearFilter}
                                                    isOptionEqualToValue={(option, value) => option === value}
                                                    onChange={(e, newValue) => setYearFilter(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Year" />
                                                    )}
                                                    sx={{ width: "100%" }}
                                                />
                                            </Stack>
                                        </Stack>
                                        {/* Chart Centered */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                flexGrow: 1,
                                                minHeight: { xs: 220, sm: 260 },
                                            }}
                                        >
                                            <Box sx={{ width: { xs: 180, sm: 220 } }}>
                                                <Pie
                                                    data={progressChartData}
                                                    options={{
                                                        plugins: {
                                                            legend: { display: false }, // ❌ remove top legend
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Bottom Stats */}
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={2}
                                            justifyContent="center"
                                            alignItems="center"
                                            sx={{ mt: 2, flexWrap: "wrap" }}
                                        >
                                            {/* Not Started */}
                                            <Box
                                                sx={{
                                                    minWidth: 120,
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: 5,
                                                    backgroundColor: "#FFEBEE",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography sx={{ color: "#F44336", fontSize: 13 }}>
                                                    Not Started
                                                </Typography>
                                                <Typography fontWeight={600}>
                                                    {progressStats.notStarted}%
                                                </Typography>
                                            </Box>

                                            {/* In Progress */}
                                            <Box
                                                sx={{
                                                    minWidth: 120,
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: 5,
                                                    backgroundColor: "#FFF8E1",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography sx={{ color: "#FFC107", fontSize: 13 }}>
                                                    In Progress
                                                </Typography>
                                                <Typography fontWeight={600}>
                                                    {progressStats.inProgress}%
                                                </Typography>
                                            </Box>

                                            {/* Completed */}
                                            <Box
                                                sx={{
                                                    minWidth: 120,
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: 5,
                                                    backgroundColor: "#E8F5E9",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography sx={{ color: "#4CAF50", fontSize: 13 }}>
                                                    Completed
                                                </Typography>
                                                <Typography fontWeight={600}>
                                                    {progressStats.completed}%
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>

                        </Grid>
                    </Box>

                    {/* Snackbar for notifications */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                        <Alert
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            severity={snackbar.severity}
                            sx={{ width: "100%" }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            )}
        </Layout>
    );
};

export default AdminDashboard;